import {useMutation} from '@tanstack/react-query';
import {ApiError, type ErrorData, type FormSubmission} from '@/app/forms/types';
import {objectToFormData} from '@/app/forms/utils';

export function useSubmit<T extends FormSubmission>(id: string) {
  return useMutation({
    mutationFn: async ({
      data,
      recaptchaToken,
    }: {
      data: T;
      recaptchaToken: string | undefined;
    }) => {
      const formData = objectToFormData({recaptchaToken, ...data});

      const response = await fetch(`/forms/api/${id}/submit/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        let errorData: Partial<ErrorData> | undefined;
        try {
          errorData = await response.json();
        } catch {
          // The error response was not valid JSON.
        }
        const message =
          errorData?.message ||
          `Form submission failed: ${response.statusText}`;
        throw new ApiError(message, {
          ...errorData,
          code: errorData?.code || 'UNKNOWN_ERROR',
          message,
        });
      }

      try {
        return await response.json();
      } catch {
        const message = 'Failed to parse success response from server.';
        throw new ApiError(message, {code: 'UNKNOWN_ERROR', message});
      }
    },
  });
}
